import { useEffect, useState, useCallback } from 'react';
import authService from './authService';
import { API_ENDPOINTS, buildApiActionUrl } from '../utils/apiConfig';

export interface UnitOverrideData {
  unitId: string;
  texts: Record<string, string>;
  images: Record<string, string>;
  videos: Record<string, string>;   // Neu: Video-Assets
  pdfs: Record<string, string>;     // Neu: PDF-Assets
  design?: any; // Für Admin-Design-Modus (flexibles Speicherformat)
  hidden?: boolean;
  updatedAt: number | null;
}

export interface UploadResult {
  asset: string;
  slot: string;
  unitId: string;
  mediaType: 'image' | 'video' | 'pdf' | 'gif';
  compressed: boolean;
  originalSize: number;
  finalSize: number;
}

export interface AllOverridesResponse {
  [unitId: string]: UnitOverrideData;
}

const API_BASE = API_ENDPOINTS.api;
const CHUNK_UPLOAD_THRESHOLD = 1800 * 1024;
const CHUNK_SIZE = 1024 * 1024;

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers || undefined);
  const token = authService.getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
  return res.json();
}

export async function getUnitOverride(unitId: string): Promise<UnitOverrideData> {
  try {
    const data = await fetchJson<UnitOverrideData>(buildApiActionUrl('getUnitOverrides', { unitId }));
    // Ensure shape
    return normalizeOverride(data, unitId);
  } catch (e) {
    return { unitId, texts: {}, images: {}, videos: {}, pdfs: {}, updatedAt: null };
  }
}

export async function getAllOverrides(): Promise<AllOverridesResponse> {
  try {
    const data = await fetchJson<AllOverridesResponse>(buildApiActionUrl('getUnitOverrides'));
    const out: AllOverridesResponse = {};
    Object.keys(data || {}).forEach(k => {
      out[k] = normalizeOverride(data[k], k);
    });
    return out;
  } catch (e) {
    return {};
  }
}

function normalizeOverride(raw: any, unitId: string): UnitOverrideData {
  // Coerce texts to a key-value object. Some backends may emit arrays.
  let texts: Record<string, string> = {};
  if (raw && raw.texts) {
    if (Array.isArray(raw.texts)) {
      // Interpret array indices as block keys: block_0, block_1, ...
      raw.texts.forEach((val: any, idx: number) => {
        if (typeof val === 'string') texts[`block_${idx}`] = val;
      });
    } else if (typeof raw.texts === 'object') {
      texts = raw.texts as Record<string, string>;
    }
  }

  // Coerce images similarly
  let images: Record<string, string> = {};
  if (raw && raw.images) {
    if (Array.isArray(raw.images)) {
      raw.images.forEach((val: any, idx: number) => {
        if (typeof val === 'string') images[`img_${idx}`] = val;
      });
    } else if (typeof raw.images === 'object') {
      images = raw.images as Record<string, string>;
    }
  }

  // Videos normalisieren
  let videos: Record<string, string> = {};
  if (raw && raw.videos) {
    if (Array.isArray(raw.videos)) {
      raw.videos.forEach((val: any, idx: number) => {
        if (typeof val === 'string') videos[`video_${idx}`] = val;
      });
    } else if (typeof raw.videos === 'object') {
      videos = raw.videos as Record<string, string>;
    }
  }

  // PDFs normalisieren
  let pdfs: Record<string, string> = {};
  if (raw && raw.pdfs) {
    if (Array.isArray(raw.pdfs)) {
      raw.pdfs.forEach((val: any, idx: number) => {
        if (typeof val === 'string') pdfs[`pdf_${idx}`] = val;
      });
    } else if (typeof raw.pdfs === 'object') {
      pdfs = raw.pdfs as Record<string, string>;
    }
  }

  const updatedAt = typeof raw?.updatedAt === 'number' ? raw.updatedAt : null;
  const design = (raw && typeof raw.design !== 'undefined') ? raw.design : undefined;
  const hidden = typeof raw?.hidden === 'boolean' ? raw.hidden : false;
  return { unitId, texts, images, videos, pdfs, design, hidden, updatedAt };
}

export function applyTextOverride(base: string, unit: string, key: string, overrides: UnitOverrideData | null): string {
  if (!overrides) return base;
  const composite = `${unit}__${key}`; // Convention for keys if needed
  if (overrides.texts[composite]) return overrides.texts[composite];
  if (overrides.texts[key]) return overrides.texts[key];
  return base;
}

// Unified image path resolution to support both stored filenames and absolute asset paths.
export function resolveImage(src: string, unit: string, slot: string, overrides: UnitOverrideData | null): string {
  if (!overrides) return src;
  const composite = `${unit}__${slot}`;
  const v = overrides.images[composite] || overrides.images[slot];
  if (!v) return src;
  const s = String(v);
  return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
}

// Video-Pfad-Auflösung
export function resolveVideo(src: string, unit: string, slot: string, overrides: UnitOverrideData | null): string {
  if (!overrides) return src;
  const composite = `${unit}__${slot}`;
  const v = overrides.videos?.[composite] || overrides.videos?.[slot];
  if (!v) return src;
  const s = String(v);
  return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
}

// PDF-Pfad-Auflösung
export function resolvePdf(src: string, unit: string, slot: string, overrides: UnitOverrideData | null): string {
  if (!overrides) return src;
  const composite = `${unit}__${slot}`;
  const v = overrides.pdfs?.[composite] || overrides.pdfs?.[slot];
  if (!v) return src;
  const s = String(v);
  return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
}

// Generischer Asset-Resolver für alle Medientypen
export function resolveAsset(slot: string, type: 'image' | 'video' | 'pdf', unit: string, overrides: UnitOverrideData | null): string | null {
  if (!overrides) return null;
  const composite = `${unit}__${slot}`;
  
  let v: string | undefined;
  if (type === 'video') {
    v = overrides.videos?.[composite] || overrides.videos?.[slot];
  } else if (type === 'pdf') {
    v = overrides.pdfs?.[composite] || overrides.pdfs?.[slot];
  } else {
    v = overrides.images?.[composite] || overrides.images?.[slot];
  }
  
  if (!v) return null;
  const s = String(v);
  return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
}

// Admin update helpers
export async function updateUnitContent(unitId: string, payload: { texts?: Record<string,string>; images?: Record<string,string>; design?: any; hidden?: boolean }, token: string): Promise<UnitOverrideData> {
  // Backend erwartet: { action, unitId, content: { texts: {...} } }
  const content: any = {};
  if (payload.texts) content.texts = payload.texts;
  if (payload.images) content.images = payload.images;
  if (payload.design) {
    content.design = payload.design;
    console.log('[FRONTEND updateUnitContent] Sending design:', JSON.stringify(payload.design).substring(0, 100));
  }
  
  const body = JSON.stringify({ 
    action: 'updateUnitContent', 
    unitId, 
    content,
    ...(typeof payload.hidden === 'boolean' ? { hidden: payload.hidden } : {})
  });
  
  console.log('[FRONTEND updateUnitContent] POST body:', body.substring(0, 200));
  
  const res = await fetch(API_BASE, { 
    method: 'POST', 
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${token}` 
    }, 
    body 
  });
  
  if (!res.ok) {
    const errorText = await res.text().catch(() => 'Unknown error');
    console.error('[FRONTEND updateUnitContent] HTTP Error:', res.status, errorText);
    throw new Error(`Update failed (${res.status}): ${errorText}`);
  }
  
  const json = await res.json();
  console.log('[FRONTEND updateUnitContent] Response:', json);
  if (!json.success) {
    throw new Error(json.error || json.message || 'Update not successful');
  }
  
  // Backend gibt override zurück - nutze es direkt
  if (json.override) {
    console.log('[FRONTEND updateUnitContent] ✅ Using override from response, has design?', !!json.override.design);
    return normalizeOverride(json.override, unitId);
  }
  
  // Fallback: neu laden wenn keine override in response
  console.log('[FRONTEND updateUnitContent] Fallback: loading fresh from server');
  return await getUnitOverride(unitId);
}

export async function uploadUnitAsset(
  unitId: string,
  slot: string,
  file: File,
  token: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  if (file.size > CHUNK_UPLOAD_THRESHOLD) {
    return uploadUnitAssetChunked(unitId, slot, file, token, onProgress);
  }

  const fd = new FormData();
  fd.append('action','uploadUnitAsset');
  fd.append('unitId', unitId);
  fd.append('slot', slot);
  fd.append('file', file);

  const xhrResult = await new Promise<{ status: number; contentType: string; responseText: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_BASE, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (event: ProgressEvent<EventTarget>) => {
      if (!onProgress || !event.lengthComputable || event.total <= 0) return;
      const percent = Math.min(100, Math.max(0, Math.round((event.loaded / event.total) * 100)));
      onProgress(percent);
    };

    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload.'));
    xhr.onload = () => {
      resolve({
        status: xhr.status,
        contentType: xhr.getResponseHeader('content-type') || '',
        responseText: xhr.responseText || ''
      });
    };

    xhr.send(fd);
  });

  if (xhrResult.status < 200 || xhrResult.status >= 300) {
    let errorText = 'Unknown error';
    let uploadErrorCode: number | null = null;
    let uploadMax = '';
    let postMax = '';

    if (xhrResult.contentType.includes('application/json')) {
      try {
        const payload = JSON.parse(xhrResult.responseText || '{}');
        errorText = payload.detail || payload.error || JSON.stringify(payload);
        uploadErrorCode = typeof payload.upload_error_code === 'number'
          ? payload.upload_error_code
          : (typeof payload.upload_error_code === 'string' && /^\d+$/.test(payload.upload_error_code)
            ? parseInt(payload.upload_error_code, 10)
            : null);
        uploadMax = payload.upload_max_filesize || '';
        postMax = payload.post_max_size || '';
      } catch {
        errorText = xhrResult.responseText || 'Unknown error';
      }
    } else {
      errorText = xhrResult.responseText || 'Unknown error';
    }

    if (uploadErrorCode === 1 || uploadErrorCode === 2) {
      const limitsInfo = [uploadMax ? `upload_max_filesize=${uploadMax}` : '', postMax ? `post_max_size=${postMax}` : '']
        .filter(Boolean)
        .join(', ');
      throw new Error(`Datei ist größer als das Upload-Limit des Servers${limitsInfo ? ` (${limitsInfo})` : ''}. Bitte Datei verkleinern oder Server-Limit erhöhen.`);
    }

    throw new Error(`Upload failed (${xhrResult.status}): ${errorText}`);
  }

  let json: any = {};
  try {
    json = JSON.parse(xhrResult.responseText || '{}');
  } catch {
    throw new Error('Ungültige Server-Antwort beim Upload.');
  }
  if (!json.success) {
    throw new Error(json.error || json.message || 'Upload not successful');
  }
  
  // Backend Response: { success, asset, slot, unitId, mediaType, compressed, originalSize, finalSize }
  return { 
    asset: json.asset || '', 
    slot: json.slot || slot, 
    unitId: json.unitId || unitId,
    mediaType: json.mediaType || 'image',
    compressed: json.compressed || false,
    originalSize: json.originalSize || file.size,
    finalSize: json.finalSize || file.size
  };
}

async function uploadUnitAssetChunked(
  unitId: string,
  slot: string,
  file: File,
  token: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const chunk = file.slice(start, end);

    const fd = new FormData();
    fd.append('action', 'uploadUnitAssetChunk');
    fd.append('unitId', unitId);
    fd.append('slot', slot);
    fd.append('uploadId', uploadId);
    fd.append('chunkIndex', String(chunkIndex));
    fd.append('totalChunks', String(totalChunks));
    fd.append('totalSize', String(file.size));
    fd.append('originalName', file.name || 'upload.bin');
    fd.append('chunk', chunk, `chunk_${chunkIndex}.part`);

    const res = await fetch(API_BASE, {
      method: 'POST',
      body: fd,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const responseText = await res.text().catch(() => '');
    let payload: any = null;
    try {
      payload = responseText ? JSON.parse(responseText) : null;
    } catch {
      payload = null;
    }

    if (!res.ok) {
      const message = payload?.detail || payload?.error || responseText || `HTTP ${res.status}`;
      throw new Error(`Upload failed (${res.status}): ${message}`);
    }

    if (!payload?.success) {
      throw new Error(payload?.error || payload?.message || 'Chunk-Upload fehlgeschlagen');
    }

    if (onProgress) {
      const percent = Math.min(100, Math.max(0, Math.round(((chunkIndex + 1) / totalChunks) * 100)));
      onProgress(percent);
    }

    if (chunkIndex === totalChunks - 1) {
      return {
        asset: payload.asset || '',
        slot: payload.slot || slot,
        unitId: payload.unitId || unitId,
        mediaType: payload.mediaType || 'image',
        compressed: payload.compressed || false,
        originalSize: payload.originalSize || file.size,
        finalSize: payload.finalSize || file.size
      };
    }
  }

  throw new Error('Chunk-Upload konnte nicht abgeschlossen werden.');
}

// Hook for convenience
export function useUnitOverride(unitId: string) {
  const [data, setData] = useState<UnitOverrideData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true); setError(null);
    getUnitOverride(unitId).then(o => { if (mounted) { setData(o); setLoading(false); } }).catch(e => { if (mounted) { setError(String(e)); setLoading(false); } });
    return () => { mounted = false; };
  }, [unitId]);

  const refresh = useCallback(() => {
    setLoading(true); setError(null);
    getUnitOverride(unitId).then(o => { setData(o); setLoading(false); }).catch(e => { setError(String(e)); setLoading(false); });
  }, [unitId]);

  return { override: data, loading, error, refresh };
}

// Simple merge helper for entire unit structure (if later extended beyond "text")
export function mergeUnitBaseWithOverride(base: any, override: UnitOverrideData | null) {
  if (!override) return base;
  // base may have a big 'text' blob. Fine-grained merging left to components.
  return { ...base, overrideMeta: { updatedAt: override.updatedAt } };
}
